import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import businessOnboardingApi from '../../api/businessOnboarding';

/**
 * Business Status Component
 * Shows verification status and capabilities
 */
export default function BusinessStatus({ businessId }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const response = await businessOnboardingApi.checkCapabilities(businessId);
        setStatus(response.data);
      } catch (err) {
        console.error('Error loading status:', err);
      } finally {
        setLoading(false);
      }
    };

    if (businessId) {
      loadStatus();
    }
  }, [businessId]);

  if (loading || !status) {
    return (
      <div className="animate-pulse h-24 bg-gray-100 rounded-lg"></div>
    );
  }

  const getStatusInfo = () => {
    if (status.canAcceptPayments) {
      return {
        icon: CheckCircleIcon,
        color: 'green',
        text: 'Payments Enabled',
        message: 'Stripe Connect onboarding is complete. Card payments are enabled.',
      };
    }

    if (status.features?.reservations || status.features?.products) {
      if (status.locationVerificationRequired) {
        return {
          icon: ClockIcon,
          color: 'yellow',
          text: 'Location Verification Required',
          message: 'Verify your business location before continuing to Stripe Connect.',
        };
      }

      return {
        icon: ClockIcon,
        color: 'yellow',
        text: 'Payments Setup Required',
        message: 'Complete Stripe Connect onboarding to accept card payments.',
      };
    }

    return {
      icon: ExclamationTriangleIcon,
      color: 'gray',
      text: 'Profile Ready',
      message: 'You can create a profile and listings. Payments are optional.',
    };
  };

  const statusInfo = getStatusInfo();
  const Icon = statusInfo.icon;

  const colorClasses = {
    green: 'bg-green-50 border-green-200 text-green-800',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
  };

  const iconColorClasses = {
    green: 'text-green-600',
    yellow: 'text-yellow-600',
    blue: 'text-blue-600',
    red: 'text-red-600',
    gray: 'text-gray-600',
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[statusInfo.color]}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-6 w-6 flex-shrink-0 ${iconColorClasses[statusInfo.color]}`} />
        <div className="flex-1">
          <h3 className="font-semibold text-sm">{statusInfo.text}</h3>
          <p className="text-xs mt-1 opacity-90">{statusInfo.message}</p>
          
          {!status.canAcceptPayments && (
            <div className="mt-3 text-xs space-y-1">
              <p className="font-medium">Available Features:</p>
              <ul className="list-disc list-inside opacity-80">
                {status.features?.reservations && <li>Reservations</li>}
                {status.features?.products && <li>Products & Payments</li>}
                {!status.features?.reservations && !status.features?.products && (
                  <li>Profile & Listings</li>
                )}
              </ul>
            </div>
          )}

          {!status.canAcceptPayments && (status.features?.reservations || status.features?.products) && status.locationVerificationRequired && (
            <div className="mt-3">
              <button
                onClick={() => navigate(`/business/${encodeURIComponent(businessId)}/onboarding/location-letter-verify`)}
                className="rounded-md bg-mainColor px-3 py-1.5 text-xs font-medium text-white"
              >
                Verify Business Location
              </button>
            </div>
          )}

          {!status.canAcceptPayments && (status.features?.reservations || status.features?.products) && !status.locationVerificationRequired && (
            <div className="mt-3">
              <button
                onClick={() => navigate(`/business/${encodeURIComponent(businessId)}/onboarding/stripe-refresh`)}
                className="rounded-md bg-mainColor px-3 py-1.5 text-xs font-medium text-white"
              >
                Connect Stripe
              </button>
            </div>
          )}

          {status.canAcceptPayments && (
            <div className="mt-3 text-xs space-y-1">
              <p className="font-medium">✓ Card Payments Unlocked</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
